import { execFile } from "node:child_process";
import { promisify } from "node:util";
import { ReviewGuideContentSchema } from "@project/shared";
import { z } from "zod";
import { defineTool } from "../harness/index.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import { getWorkbenchClient } from "../workbench/client.js";
import { workbenchEnabled } from "../workbench/mode.js";
import { textResult } from "./helpers.js";

async function endReviewSession(
  connection: AgentConnection,
  reason: "approved" | "changes_requested",
): Promise<void> {
  await connection.call("endReviewSession", {
    sessionId: connection.sessionId,
    reason,
  });
}

// Canonical risk levels — mirror RISK_LEVELS in @project/shared (conveyor-agent
// is published standalone without the shared dep). The reviewer MUST choose one
// on every verdict; the server applies it authoritatively (may raise OR lower an
// already-set value).
const RISK_LEVELS = ["critical", "high", "medium", "low"] as const;
const reviewedShaSchema = z
  .string()
  .regex(/^[0-9a-f]{40}$/i)
  .describe("REQUIRED. The full 40-character commit SHA this verdict reviews.");

const riskDescription =
  "REQUIRED. The risk level this change carries, judged by the surface area it touches: " +
  "critical = touches critical/foundational surface, high = important surface, medium = moderate, " +
  "low = small/isolated. Set this on every verdict. You have authority to override a risk level " +
  "already set on the task if you disagree with it.";

// @project/shared uses Zod 3 while the agent MCP harness uses Zod 4. Keep the
// MCP-facing shape local, then validate its content with the shared schema in
// the handler before sending it over the agent-session boundary.
const ReviewGuideToolSchema = z.strictObject({
  reviewedSha: z
    .string()
    .regex(/^[0-9a-f]{40}$/i)
    .describe(
      "REQUIRED. The PR's current head as a full 40-char SHA. Run `git rev-parse HEAD` " +
        "immediately before this call — never extend an abbreviated hash into 40 characters.",
    ),
  overview: z
    .string()
    .min(1)
    // The real limit is 3000, enforced by ReviewGuideContentSchema after
    // flattened-payload recovery. The boundary cap is deliberately loose so a
    // mis-encoded call (see recoverFlattenedGuide) reaches the handler and can
    // be repaired, instead of dying as an opaque MCP validation error.
    .max(60_000)
    .describe("REQUIRED. Plain-text walkthrough intro, max 3000 characters. Keep it short."),
  sections: z
    .array(
      z.strictObject({
        title: z.string().min(1).max(160),
        explanation: z.string().min(1).max(2_000),
        classification: z.enum(["core", "supporting"]).optional(),
        files: z
          .array(
            z.strictObject({
              path: z
                .string()
                .min(1)
                .max(500)
                .describe(
                  "A file the PR's diff actually changed. Context files you merely read are rejected.",
                ),
              startLine: z.number().int().positive().max(1_000_000).optional(),
              endLine: z.number().int().positive().max(1_000_000).optional(),
              hunkHeader: z
                .string()
                .min(1)
                .max(300)
                .optional()
                .describe(
                  "Optional anchor, matched byte-exactly against the full hunk header line from " +
                    "`git diff` INCLUDING the context text after the second @@. Copy it verbatim " +
                    "from `git diff <base>..HEAD -- <file> | grep '^@@'`, or omit anchors entirely " +
                    "(path-only entries always validate).",
                ),
            }),
          )
          .min(1)
          .max(20),
      }),
    )
    .min(1)
    .max(12)
    .optional()
    .describe(
      "REQUIRED top-level array (never text appended to overview) of ordered conceptual sections.",
    ),
});

// A malformed tool-call encoding can deliver the entire call as a single
// `overview` string, with the following parameter's delimiter embedded
// verbatim — e.g. `...prose</overview>\n<parameter name="sections">[{...}]`.
// 30 of 113 fleet calls in 24h arrived this way and were rejected as
// "sections: expected array, received undefined"; two sessions never managed
// to publish at all. Recover the real arguments instead of failing, and keep
// this defensive until the encoding defect itself is fixed upstream.
const FLATTENED_SECTIONS_PATTERN = /<\/overview>\s*<parameter name="sections">/;

export function recoverFlattenedGuide(overview: string): {
  overview: string;
  sections: unknown;
} | null {
  const match = FLATTENED_SECTIONS_PATTERN.exec(overview);
  if (!match) return null;
  const head = overview.slice(0, match.index);
  const tail = overview
    .slice(match.index + match[0].length)
    .replace(/<\/parameter>\s*$/, "")
    .trim();
  try {
    return { overview: head.trim(), sections: JSON.parse(tail) };
  } catch {
    return null;
  }
}

// Agents hand-expanded short SHAs from `git log --oneline` into 40 characters,
// which the server rejects as "head does not match the reviewed SHA". Resolving
// HEAD locally removes the failure mode rather than warning about it.
//
// Split-mode pods run this tool's handler in the agent (launcher) container,
// but the actual repo checkout lives in the workbench container — a bare
// `execFile` here would run `git rev-parse` against a directory with no repo
// and silently fail every time. Route through the workbench client when the
// split is enabled, mirroring `git()` in runner/git-utils.ts.
export async function resolveGitHeadSha(cwd: string): Promise<string | null> {
  try {
    const stdout = workbenchEnabled()
      ? (
          await getWorkbenchClient().execFile("git", ["rev-parse", "HEAD"], {
            cwd,
            timeout: 10_000,
          })
        ).stdout
      : (await promisify(execFile)("git", ["rev-parse", "HEAD"], { cwd, timeout: 10_000 })).stdout;
    const sha = stdout.trim();
    return /^[0-9a-f]{40}$/i.test(sha) ? sha : null;
  } catch {
    return null;
  }
}

export interface PublishReviewGuideOptions {
  /**
   * Resolves the repo's real HEAD. Injected rather than defaulted so the tool
   * stays deterministic under test; the production call site (tools/index.ts)
   * passes `resolveGitHeadSha`. When absent, the agent-supplied SHA is used.
   */
  resolveHeadSha?: () => Promise<string | null>;
}

export function buildPublishReviewGuideTool(
  connection: AgentConnection,
  options: PublishReviewGuideOptions = {},
) {
  const { resolveHeadSha } = options;
  return defineTool(
    "publish_review_guide",
    "Publish or update the PR guide for the CURRENT PR head commit. Call right after create_pull_request succeeds, and again after every push to the branch. The head SHA is resolved from the local repo, so never hand-expand a short hash. Best-effort — it does not gate opening or updating the PR.",
    ReviewGuideToolSchema.shape,
    async ({ reviewedSha, overview, sections }) => {
      let resolvedOverview = overview;
      let resolvedSections: unknown = sections;
      if (resolvedSections === undefined) {
        const recovered = recoverFlattenedGuide(overview);
        if (!recovered) {
          throw new Error(
            "publish_review_guide requires a top-level `sections` array — an ordered list of " +
              "conceptual sections, each with title, explanation, and files[]. Do not append the " +
              "sections to `overview` as text. Retry with sections as a real array argument.",
          );
        }
        resolvedOverview = recovered.overview;
        resolvedSections = recovered.sections;
      }
      const content = ReviewGuideContentSchema.parse({
        overview: resolvedOverview,
        sections: resolvedSections,
      });
      const result = await connection.call("publishReviewGuide", {
        sessionId: connection.sessionId,
        reviewedSha: (resolveHeadSha ? await resolveHeadSha() : null) ?? reviewedSha,
        ...content,
      });
      return textResult(
        `Review guide published for ${result.reviewedSha}${result.replaced ? " (replaced)" : ""}.`,
      );
    },
    { strict: true },
  );
}

function buildApproveCodeReviewTool(connection: AgentConnection) {
  return defineTool(
    "approve_code_review",
    "Approve the code review and exit. Use when the diff passes all review criteria. Requires a summary and a risk level — for changes, use request_code_changes with a structured issues[] list.",
    {
      reviewedSha: reviewedShaSchema,
      summary: z.string().describe("Brief summary of what was reviewed and why it looks good"),
      risk: z.enum(RISK_LEVELS).describe(riskDescription),
    },
    async ({ reviewedSha, summary, risk }) => {
      const content = `**Code Review: Approved** :white_check_mark:\n\n${summary}`;
      const result = await connection.call("submitCodeReviewResult", {
        sessionId: connection.sessionId,
        reviewedSha,
        approved: true,
        content,
        risk,
      });
      if (result.applied === false) {
        await endReviewSession(connection, "approved");
        return textResult(
          "This review was superseded by a newer review cycle. No verdict was applied.",
        );
      }
      connection.sendEvent({ type: "code_review_complete", result: "approved", summary });
      await endReviewSession(connection, "approved");
      return textResult("Code review approved. Exiting.");
    },
  );
}

function buildRequestCodeChangesTool(connection: AgentConnection) {
  return defineTool(
    "request_code_changes",
    "Request changes during code review and exit. Use when substantive issues must be fixed before merge. Each issue: { file, line?, severity: critical|major|minor, description }.",
    {
      reviewedSha: reviewedShaSchema,
      issues: z
        .array(
          z.object({
            file: z.string().describe("File path where the issue was found"),
            line: z.number().optional().describe("Line number (if applicable)"),
            severity: z.enum(["critical", "major", "minor"]).describe("Issue severity"),
            description: z.string().describe("What is wrong and how to fix it"),
          }),
        )
        .describe("List of issues found during review"),
      summary: z.string().describe("Brief overall summary of the review findings"),
      risk: z.enum(RISK_LEVELS).describe(riskDescription),
    },
    async ({ reviewedSha, issues, summary, risk }) => {
      const issueLines = issues
        .map((issue) => {
          const loc = issue.line ? `:${issue.line}` : "";
          return `- **[${issue.severity}]** \`${issue.file}${loc}\`: ${issue.description}`;
        })
        .join("\n");
      const content = `**Code Review: Changes Requested** :warning:\n\n${summary}\n\n${issueLines}`;
      const result = await connection.call("submitCodeReviewResult", {
        sessionId: connection.sessionId,
        reviewedSha,
        approved: false,
        content,
        risk,
      });
      if (result.applied === false) {
        await endReviewSession(connection, "changes_requested");
        return textResult(
          "This review was superseded by a newer review cycle. No verdict was applied.",
        );
      }
      connection.sendEvent({
        type: "code_review_complete",
        result: "changes_requested",
        summary,
        issues,
      });
      await endReviewSession(connection, "changes_requested");
      return textResult("Code review complete — changes requested. Exiting.");
    },
  );
}

export function buildCodeReviewTools(connection: AgentConnection) {
  return [buildApproveCodeReviewTool(connection), buildRequestCodeChangesTool(connection)];
}
